import { Slide } from '@mui/material';
import { useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  Box,
  List,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  useTheme,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import UserSettingsModal, { type UserSettingsMenuKey } from './user-settings-modal';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '@/i18n/use-translations';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useUserStore } from '@/state/stores/use-user-store';

const legalLinks = [
  { to: '/legal', label: { en: 'Legal Notice', de: 'Impressum' } },
  { to: '/legal/privacy', label: { en: 'Privacy', de: 'Datenschutz' } },
  { to: '/legal/cookies', label: { en: 'Cookie Policy', de: 'Cookie-Richtlinie' } },
  { to: '/legal/terms', label: { en: 'Terms of Use', de: 'Nutzungsbedingungen' } },
] as const;

interface UserMenuProps {
  open: boolean;
  onClose: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ open, onClose }) => {
  const t = useTranslations();
  const { i18n } = useTranslation();
  const legalLang = i18n.language?.startsWith('de') ? 'de' : 'en';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const { signOut } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerClose, setisDrawerClose] = useState(false);
  const setActiveOrganization = useActiveOrganizationStore((s) => s.setActiveOrganization);
  const [menuSelected, setMenuSelected] = useState<UserSettingsMenuKey>('general');
  const { user: clerkUser } = useUser();
  const userAvatar = clerkUser?.imageUrl || '';
  const user = useUserStore((s) => s.user);

  const handleOpenSettingsModal = () => {
    setisDrawerClose(true);
    setMenuSelected('general');
    setIsModalOpen(true);
  };

  const handleCloseSettingsModal = () => {
    onClose();
  };

  const menuItems = [
    {
      text: t('userMenu.settings'),
      icon: <ManageAccountsOutlinedIcon />,
      onClick: handleOpenSettingsModal,
      dataTestId: 'user-menu-settings-button',
    },
  ];

  return (
    <>
      {!isDrawerClose && (
        <Slide direction="left" in={open} mountOnEnter unmountOnExit timeout={600}>
          <Box
            sx={{
              position: 'fixed',
              top: 16,
              right: 16,
              width: '200px',
              backgroundColor: isDarkMode ? theme.palette.custom.backgroundColor : 'background.paper',
              boxShadow: 6,
              borderRadius: 2,
              zIndex: 1300,
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
              pt: 2,
              pr: 1,
              pl: 1,
              pb: 0.5,
            }}
          >
            <Card
              sx={{
                borderRadius: 2,
                justifyContent: 'center',
                alignItems: 'center',
                maxWidth: '200px',
                boxShadow: 'none',
                backgroundColor: isDarkMode ? theme.palette.custom.backgroundColor : 'background.paper',
              }}
            >
              <CardContent
                sx={{
                  'padding': '0px',
                  '&.MuiCardContent-root': {
                    paddingBottom: 0,
                  },
                  'display': 'flex',
                  'flexDirection': 'column',
                  'backgroundColor': isDarkMode ? theme.palette.custom.backgroundColor : 'background.paper',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 0,
                    pl: 1,
                  }}
                >
                  <Avatar src={userAvatar} alt="User Avatar" sx={{ width: 32, height: 32, mr: 1.5 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '120px',
                      height: 'auto',
                    }}
                  >
                    {user?.firstName} {user?.lastName}
                  </Typography>
                </Box>

                <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {menuItems.map(({ text, icon, onClick, dataTestId }, index) => (
                    <ListItemButton
                      key={index}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                      }}
                      data-testid={dataTestId}
                      onClick={() => {
                        onClick();
                        if (text !== t('userMenu.settings')) {
                          onClose();
                        }
                      }}
                    >
                      <ListItemIcon>{icon}</ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                            {text}
                          </Typography>
                        }
                        sx={{
                          textTransform: 'capitalize',
                          color: 'text.primary',
                          mx: 1,
                        }}
                      />
                    </ListItemButton>
                  ))}

                  <Divider sx={{ width: '100%' }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, px: 1.5, py: 0.5 }}>
                    {legalLinks.map((link) => (
                      <Link
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        underline="hover"
                        variant="caption"
                        color="text.secondary"
                        onClick={onClose}
                      >
                        {link.label[legalLang]}
                      </Link>
                    ))}
                  </Box>

                  <Divider sx={{ width: '100%' }} />

                  <ListItemButton
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 0,
                    }}
                    onClick={() => {
                      signOut();
                      onClose();
                      setActiveOrganization(undefined);
                    }}
                  >
                    <ListItemIcon>
                      <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', color: 'text.primary' }}>
                          {t('userMenu.logout')}
                        </Typography>
                      }
                      sx={{ mx: 1 }}
                    />
                  </ListItemButton>
                </List>
              </CardContent>
            </Card>
          </Box>
        </Slide>
      )}

      <UserSettingsModal
        open={isModalOpen}
        close={handleCloseSettingsModal}
        menuSelected={menuSelected}
        setMenuSelected={setMenuSelected}
      />
    </>
  );
};

export default UserMenu;
